import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, Briefcase, Calendar, Star, ExternalLink, Clock, Eye, AlertCircle } from 'lucide-react';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public Shared Record Page
 * 
 * Displays a single verified record via a share token.
 * No authentication required.
 */
export default function SharedRecord() {
  const { token } = useParams();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const response = await fetch(`${API_URL}/api/sharing/records/${token}`);
        
        if (response.status === 404) {
          setError('This share link is invalid or has been revoked');
          return;
        }
        
        if (response.status === 410) {
          setError('This share link has expired');
          return;
        }
        
        if (!response.ok) {
          setError('Failed to load record');
          return;
        }
        
        const result = await response.json();
        setData(result);
      } catch (err) {
        console.error('Error fetching shared record:', err);
        setError('Failed to load record');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchRecord();
    }
  }, [token]);

  // Get tier color
  const getTierColor = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'distinguished': return '#F0B429';
      case 'established': return '#00C9A7';
      case 'verified': return '#1B5AFF';
      default: return '#5B6472';
    }
  };

  // Get tier badge
  const getTierBadge = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'distinguished': return '✦';
      case 'established': return '●';
      case 'verified': return '✓';
      default: return '○';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F8FF] flex items-center justify-center">
        <div className="animate-pulse flex items-center gap-2 text-[#00C9A7]">
          <Shield className="w-8 h-8" />
          <span className="text-lg">Loading record...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Record Not Found - VETT</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="min-h-screen bg-[#F5F8FF] flex items-center justify-center p-4">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 max-w-md text-center">
            <AlertCircle className="w-12 h-12 text-[#1B5AFF] mx-auto mb-4" />
            <h1 className="text-xl font-bold text-[#0A0F1E] mb-2">Link Invalid or Expired</h1>
            <p className="text-[#5B6472] mb-6">{error}</p>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-[#00C9A7] hover:text-[#00E5BF]"
            >
              <ExternalLink className="w-4 h-4" />
              Go to VETT Homepage
            </Link>
          </Card>
        </div>
      </>
    );
  }

  const { record, professional, share_info } = data;
  const tierColor = getTierColor(professional?.tier);

  return (
    <>
      <Helmet>
        <title>{record?.job_title} at {record?.employer_name} - Verified by VETT</title>
        <meta name="description" content={`Verified employment record: ${record?.job_title} at ${record?.employer_name}`} />
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-[#F5F8FF]" data-testid="shared-record-page">
        {/* Header */}
        <header className="border-b border-[#E8EDF5] bg-[#FFFFFF]">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Shield className="w-8 h-8 text-[#00C9A7]" />
              <span className="text-xl font-bold text-[#0A0F1E]">VETT</span>
            </Link>
            <Link 
              to="/signup"
              className="text-sm px-4 py-2 bg-[#1B5AFF] text-white rounded-lg hover:bg-[#1450E6] transition-colors"
            >
              Create Your VETT ID
            </Link>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto">
            {/* Shared Record Notice */}
            <div className="flex items-center gap-3 p-4 mb-6 bg-[#1B5AFF]/10 border border-[#1B5AFF]/30 rounded-xl">
              <Shield className="w-5 h-5 text-[#1B5AFF]" />
              <span className="text-[#1B5AFF] text-sm font-medium">
                This is a verified employment record shared via VETT
              </span>
            </div>

            {/* Professional Card */}
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 mb-6">
              <div className="flex items-center gap-4">
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center text-xl font-mono font-bold text-[#0A0F1E]"
                  style={{ background: `${tierColor}30` }}
                >
                  {professional?.first_name?.[0]}{professional?.last_initial}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h2 className="text-lg font-bold text-[#0A0F1E]">
                      {professional?.first_name} {professional?.last_initial}.
                    </h2>
                    <span 
                      className="px-3 py-1 rounded-md font-mono text-xs"
                      style={{ background: `${tierColor}20`, color: tierColor }}
                    >
                      {getTierBadge(professional?.tier)} {professional?.tier}
                    </span>
                  </div>
                  <p className="text-[#5B6472] text-sm">{professional?.headline}</p>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#0A0F1E] font-mono">{professional?.score || 0}</div>
                  <div className="text-xs text-[#6666AA]">VETT Score</div>
                </div>
              </div>
              
              {professional?.vett_id && (
                <div className="mt-4 pt-4 border-t border-[#E8EDF5] flex items-center justify-between">
                  <span className="text-sm text-[#6666AA]">VETT ID</span>
                  <Link 
                    to={`/id/${professional.vett_id}`}
                    className="text-[#00C9A7] text-sm font-mono hover:underline"
                  >
                    {professional.vett_id}
                  </Link>
                </div>
              )}
            </Card>

            {/* Record Card */}
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-xl font-bold text-[#0A0F1E] mb-1" data-testid="record-title">
                      {record?.job_title}
                    </h1>
                    <p className="text-[#5B6472]">{record?.employer_name}</p>
                  </div>
                  {record?.rating && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-[#F0B429]/10 rounded-lg">
                      <Star className="w-5 h-5 text-[#F0B429]" fill="#F0B429" />
                      <span className="text-lg font-semibold text-[#F0B429]">{record.rating.toFixed(1)}</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-4 mb-6 text-sm text-[#6666AA]">
                  {record?.department && (
                    <span className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      {record.department}
                    </span>
                  )}
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {record?.start_date?.slice(0, 7)} – {record?.end_date?.slice(0, 7) || 'Present'}
                  </span>
                </div>

                {record?.closing_statement && (
                  <div className="p-4 bg-[#F5F8FF] rounded-xl border-l-4 border-[#00C9A7]">
                    <p className="text-[#5B6472] italic text-sm leading-relaxed">
                      "{record.closing_statement}"
                    </p>
                  </div>
                )}
              </div>

              {/* Verification Footer */}
              <div className="px-6 py-4 bg-[#F5F8FF] border-t border-[#E8EDF5]">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-[#00C9A7]">
                    <Shield className="w-4 h-4" />
                    <span>Verified Record</span>
                  </div>
                  {record?.acknowledged_at && (
                    <span className="text-[#6666AA]">
                      Co-signed {new Date(record.acknowledged_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </Card>

            {/* Share Info */}
            <div className="mt-6 flex items-center justify-between text-xs text-[#6666AA]">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {share_info?.views || 1} view{share_info?.views !== 1 ? 's' : ''}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Expires {new Date(share_info?.expires_at).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Footer CTA */}
            <div className="mt-8 text-center">
              <p className="text-[#6666AA] text-sm mb-4">
                VETT provides verified career credentials that employers can trust.
              </p>
              <Link 
                to="/signup" 
                className="inline-flex items-center gap-2 text-[#00C9A7] hover:text-[#00E5BF] text-sm"
              >
                Create your own verified profile
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-[#E8EDF5] mt-12">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-[#5B6472]">
                <Shield className="w-4 h-4" />
                <span className="text-sm">VETT - Verified Employment & Training Trust</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-[#6666AA]">
                <Link to="/privacy" className="hover:text-[#5B6472]">Privacy</Link>
                <Link to="/terms" className="hover:text-[#5B6472]">Terms</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
